import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import DeliveryInformation from './pages/DeliveryInformation';
import DeliveryInformationV2 from './pages/DeliveryInformationV2';
import Menu from './pages/Menu';
import NewOrder from './pages/NewOrder';
import NewOrderV2 from './pages/NewOrderV2';
import NotFound from './pages/NotFound';
import OrderDetail from './pages/OrderDetail';
import Welcome from './pages/Welcome';

import { I18nProvider } from './context/I18n/I18n';
import { SessionProvider } from './context/SessionContext';
import { SessionGate } from './context/SessionGate';

import { CircularProgress } from '@mui/material';
import React from 'react';
import { MenuProvider } from './context/MenuContext';
import { NewOrderProvider } from './context/NewOrderContext';
import { OrderProvider } from './context/OrderContext';
import { SystemProvider, useSystemContext } from './context/SystemContext/SystemContext';
import { UserMenuProvider } from './context/UserMenuContext';
import DownloadTicket from './pages/DownloadTicket';
import OrderDetailV2 from './pages/OrderDetailV2';
import PrivacyPolicy from './pages/PrivacyPolicy';

const queryClient = new QueryClient();

const AppContent: React.FC = () => {
  const { enabledNewVersion, isLoadingStadium } = useSystemContext();

  if (isLoadingStadium) {
    return (
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <CircularProgress color="success" size={65} />
      </div>
    );
  }

  const content = enabledNewVersion ? (
    <UserMenuProvider>
      <NewOrderProvider>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/neworder" element={<NewOrderV2 />} />
          <Route path="/delivery" element={<DeliveryInformationV2 />} />
          <Route path="/menu/:id" element={<Menu />} />
          <Route path="/orderdetail/:id" element={<OrderDetailV2 />} />
          <Route path="/download-ticket" element={<DownloadTicket />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </NewOrderProvider>
    </UserMenuProvider>
  ) : (
    <MenuProvider>
      <OrderProvider>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/neworder" element={<NewOrder />} />
          <Route path="/delivery" element={<DeliveryInformation />} />
          <Route path="/menu/:id" element={<Menu />} />
          <Route path="/orderdetail/:id" element={<OrderDetail />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </OrderProvider>
    </MenuProvider>
  );

  if (!enabledNewVersion) {
    return (
      <SessionProvider>
        <SessionGate>{content}</SessionGate>
      </SessionProvider>
    );
  }

  return content;
};

export default function App() {
  return (
    <div className="font-vn" style={{ height: '100dvh' }}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/privacy-policy"
            element={
              <QueryClientProvider client={queryClient}>
                <I18nProvider>
                  <PrivacyPolicy />
                </I18nProvider>
              </QueryClientProvider>
            }
          />
          <Route
            path="/*"
            element={
              <QueryClientProvider client={queryClient}>
                <I18nProvider>
                  <SystemProvider>
                    <AppContent />
                  </SystemProvider>
                </I18nProvider>
              </QueryClientProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}
